import { siteText, speakerCredit } from '../siteText'
import { useEffect, useRef, useState } from 'react'
import { ArrowUp, ArrowUpRight, Film, Play, WandSparkles, X } from 'lucide-react'
import { api, ApiRequestError } from '../api'
import type { AssistantClip, AssistantConversation, AssistantEvent, AssistantPendingAction } from '../types'
import type { Route } from './AppShell'
import { formatDuration } from './ui'
import { useMediaRecovery } from '../mediaRecovery'

type Message = {
  id: string
  role: 'user' | 'assistant'
  text: string
  clips?: AssistantClip[]
  jobs?: NonNullable<AssistantEvent['job']>[]
  pending?: AssistantPendingAction[]
  receipts?: NonNullable<AssistantEvent['action_result']>[]
}

export function MotionAssistant({ route, currentClipId, onRoute, onClip, onSource }: { route: Route; currentClipId: string | null; onRoute: (route: Route) => void; onClip: (id: string) => void; onSource: (id: string) => void }) {
  const [open, setOpen] = useState(false)
  const [closing, setClosing] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [busy, setBusy] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [playingClip, setPlayingClip] = useState<string | null>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const logRef = useRef<HTMLDivElement>(null)
  const launchRef = useRef<HTMLButtonElement>(null)
  const hasOpened = useRef(false)
  // Session-only: reopening the app must never restore a previous chat.
  const conversation = useRef<string | null>(null)
  const newConversation = useRef<Promise<AssistantConversation> | null>(null)
  const activeTurn = useRef<string | null>(null)
  const reconnectNeeded = useRef(false)
  const controller = useRef<AbortController | null>(null)
  const [status, setStatus] = useState('')
  const [loaded, setLoaded] = useState(false)
  useEffect(() => () => controller.current?.abort(), [])

  useEffect(() => {
    if (open) { hasOpened.current = true; inputRef.current?.focus() }
    else { setPlayingClip(null); if (hasOpened.current) launchRef.current?.focus() }
  }, [open])
  useEffect(() => { logRef.current?.scrollTo({ top: logRef.current.scrollHeight }) }, [messages, busy])
  useEffect(() => {
    if (!open) return
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setClosing(true) }
    window.addEventListener('keydown', escape)
    return () => window.removeEventListener('keydown', escape)
  }, [open])

  const pendingJobs = [...new Set(messages.flatMap((message) => (message.jobs || [])
    .filter((job) => job.status === 'queued' || job.status === 'running').map((job) => job.id)))].sort().join(',')
  useEffect(() => {
    if (!open || !pendingJobs) return
    let alive = true
    const refresh = async () => {
      try {
        const jobs = new Map((await api.jobStatuses({ job_ids: pendingJobs.split(',') })).map((job) => [job.id, job]))
        if (alive) setMessages((items) => items.map((message) => ({ ...message,
          jobs: message.jobs?.map((job) => jobs.get(job.id) || job),
        })))
      } catch { /* Keep the last confirmed state until the next refresh. */ }
    }
    void refresh()
    const timer = window.setInterval(() => void refresh(), 2500)
    return () => { alive = false; window.clearInterval(timer) }
  }, [open, pendingJobs])

  function updateReply(turn: string, event: AssistantEvent) {
    if (event.type === 'status') setStatus(event.text || 'Working…')
    if (event.type === 'navigation') {
      if (event.route && ['home', 'review', 'calendar', 'editor', 'final', 'projects', 'sources', 'library', 'settings'].includes(event.route)) onRoute(event.route)
      if (event.source_id) onSource(event.source_id)
    }
    setMessages((items) => items.map((message) => {
      if (message.id !== turn || message.role !== 'assistant') return message
      if (event.type === 'text') return { ...message, text: message.text + (event.text || '') }
      if (event.type === 'clips') return { ...message, clips: event.clips }
      if (event.type === 'job' && event.job) return { ...message, jobs: [...(message.jobs || []).filter((j) => j.id !== event.job!.id), event.job] }
      if (event.type === 'pending_action' && event.pending_action) {
        return {
          ...message,
          pending: [...(message.pending || []).filter((pending) => pending.nonce !== event.pending_action!.nonce), event.pending_action],
        }
      }
      if (event.type === 'action_result' && event.action_result) {
        const job = event.action_result.job
        return {
          ...message,
          pending: message.pending?.map((pending) => pending.nonce === event.action_result!.nonce
            ? { ...pending, status: event.action_result!.status === 'confirmed' ? 'confirmed' : event.action_result!.status }
            : pending),
          jobs: job ? [...(message.jobs || []).filter((item) => item.id !== job.id), job] : message.jobs,
          receipts: [...(message.receipts || []).filter((item) => item.nonce !== event.action_result!.nonce), event.action_result],
        }
      }
      if (event.type === 'error' || event.type === 'done' && event.text) return { ...message, text: event.text || message.text }
      return message
    }))
  }

  async function observe(cid: string, turn: string, after = 0) {
    const abort = new AbortController()
    controller.current = abort
    activeTurn.current = turn
    setBusy(true)
    let cursor = after
    try {
      // Reconnect to the existing turn rather than submitting another mutation.
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          await api.assistantStream(cid, turn, cursor, abort.signal, (event) => {
            if (event.seq && event.seq <= cursor) return
            cursor = event.seq || cursor
            updateReply(turn, event)
          })
          break
        } catch (error) {
          if (abort.signal.aborted) return
          if (attempt === 1) throw error
        }
      }
    } catch (error) {
      updateReply(turn, { type: 'error', text: 'Connection interrupted. Close and reopen this chat to reconnect.' })
      reconnectNeeded.current = true
    } finally {
      activeTurn.current = null
      setBusy(false)
      setStatus('')
    }
  }

  function restore(saved: AssistantConversation) {
    conversation.current = saved.id
    setMessages(saved.turns.flatMap((turn): Message[] => {
      const clips = turn.events.filter((event) => event.type === 'clips').at(-1)?.clips
      const jobs = new Map(turn.events.flatMap((event) => {
        const job = event.type === 'job' ? event.job : event.type === 'action_result' ? event.action_result?.job : undefined
        return job ? [[job.id, job] as const] : []
      }))
      const pending = turn.events
        .filter((event) => event.type === 'pending_action' && event.pending_action)
        .reduce<AssistantPendingAction[]>((items, event) => {
          const action = event.pending_action!
          return [...items.filter((item) => item.nonce !== action.nonce), action]
        }, [])
      const text = turn.text || turn.events.filter((event) => event.type === 'text').map((event) => event.text || '').join('')
      const receipts = [...new Map(turn.events.filter((event) => event.type === 'action_result' && event.action_result)
        .map((event) => [event.action_result!.nonce, event.action_result!])).values()]
      return [{ id: turn.id, role: 'user', text: turn.prompt }, { id: turn.id, role: 'assistant', text, clips, jobs: [...jobs.values()], pending, receipts }]
    }))
    const running = saved.turns.find((turn) => turn.status === 'running')
    if (running) void observe(saved.id, running.id, Math.max(0, ...running.events.map((event) => event.seq || 0)))
  }

  useEffect(() => {
    if (!open) {
      if (reconnectNeeded.current) { setLoaded(false); reconnectNeeded.current = false }
      return
    }
    // The stream can fail after the panel has already closed, so the closed
    // effect may never see this flag. Reopening must check it too.
    if (reconnectNeeded.current) {
      reconnectNeeded.current = false
      if (loaded) { setLoaded(false); return }
    }
    if (loaded) return
    let alive = true
    // Reconnect only to this window's conversation. Retain an in-flight creation
    // when the panel closes, so reopening it cannot create a duplicate session.
    const load = conversation.current
      ? api.assistantHistory(conversation.current)
      : (newConversation.current ||= api.assistantConversation().catch((error) => {
        newConversation.current = null
        throw error
      }))
    void load.then((value) => {
      if (alive) { restore(value); setLoaded(true); setStatus('') }
    }).catch(() => { if (alive) setStatus('Could not load this conversation. Close and reopen this chat to retry.') })
    return () => { alive = false }
  }, [open, loaded])

  async function submit() {
    const text = prompt.trim()
    const cid = conversation.current
    if (!text || busy || activeTurn.current || !cid) return
    const turn = crypto.randomUUID()
    activeTurn.current = turn
    setMessages((items) => [...items, { id: turn, role: 'user', text }, { id: turn, role: 'assistant', text: '' }])
    setPrompt('')
    setBusy(true)
    setStatus('Thinking…')
    const body = { conversation_id: cid, turn_id: turn, prompt: text, page: route, current_clip_id: currentClipId }
    try {
      try { await api.assistant(body) }
      catch (error) { if (error instanceof TypeError) await api.assistant(body); else throw error }
      await observe(cid, turn)
    } catch (error) {
      updateReply(turn, { type: 'error', text: error instanceof Error ? error.message : 'Motion could not complete that request.' })
    } finally {
      activeTurn.current = null
      setBusy(false)
      setStatus('')
      inputRef.current?.focus()
    }
  }

  async function stop() {
    if (!conversation.current || !activeTurn.current) return
    try { await api.assistantCancel(conversation.current, activeTurn.current) }
    catch { setStatus('Could not stop this response. Please try again.') }
  }

  async function confirmPending(turn: string, pending: AssistantPendingAction) {
    const cid = conversation.current
    if (!cid || busy || pending.status === 'confirming' || pending.status === 'confirmed') return
    setStatus('')
    setMessages((items) => items.map((message) => message.id === turn && message.role === 'assistant'
      ? { ...message, pending: message.pending?.map((item) => item.nonce === pending.nonce ? { ...item, status: 'confirming' } : item) }
      : message))
    try {
      const result = await api.assistantConfirm(cid, turn, pending.nonce)
      updateReply(turn, { type: 'action_result', action_result: result })
      setMessages((items) => items.map((message) => message.id === turn && message.role === 'assistant'
        ? {
            ...message,
            pending: message.pending?.map((item) => item.nonce === pending.nonce
              ? { ...item, status: result.status === 'confirmed' ? 'confirmed' : result.status }
              : item),
            jobs: result.job ? [...(message.jobs || []).filter((job) => job.id !== result.job!.id), result.job] : message.jobs,
          }
        : message))
    } catch (error) {
      // The server may have committed before the response was lost. Recover
      // its receipt before offering a retry; replaying this same nonce is
      // idempotent if history is temporarily unavailable too.
      try {
        const saved = await api.assistantHistory(cid)
        const events = saved.turns.find((item) => item.id === turn)?.events || []
        const action = events.filter((event) => event.type === 'pending_action' && event.pending_action?.nonce === pending.nonce).at(-1)
        if (action?.pending_action?.status === 'confirmed') {
          updateReply(turn, action)
          for (const event of events) {
            if (event.type === 'action_result' && event.action_result?.nonce === pending.nonce) updateReply(turn, event)
          }
          setStatus('')
          return
        }
      } catch { /* A retry still uses the original server-stored nonce. */ }
      const unavailable = error instanceof ApiRequestError && (error.status === 404 || error.status === 409 && /expired|clip changed|no longer available/i.test(error.message))
      setMessages((items) => items.map((message) => message.id === turn && message.role === 'assistant'
        ? { ...message, pending: message.pending?.map((item) => item.nonce === pending.nonce ? { ...item, status: unavailable ? 'failed' : 'pending' } : item) }
        : message))
      setStatus(error instanceof Error ? error.message : 'Could not confirm this action.')
    }
  }

  return <div className="motion-assistant">
    {open ? <section className={`assistant-panel${closing ? ' assistant-panel-closing' : ''}`} role="dialog" aria-label="Motion AI assistant" onAnimationEnd={(event) => { if (closing && event.target === event.currentTarget) { setOpen(false); setClosing(false) } }}>
      <div className="assistant-header">
      <button className="assistant-close" type="button" aria-label="Close Motion AI" onClick={() => setClosing(true)}><X size={20} /></button>
      </div>
      <div className="assistant-log" ref={logRef} role="log" aria-live="polite">
        {messages.length === 0 ? <div className="assistant-welcome" aria-label="Motion AI"><span className="motion-logo" aria-hidden="true" /></div> : null}
        {messages.map((message, index) => <div key={`${message.id}:${message.role}`} className={`assistant-response assistant-response-${message.role}`}>
          {message.text ? <div className={`assistant-message assistant-message-${message.role}`}>{siteText(message.text)}</div> : null}
          {message.pending?.filter((pending) => pending.status !== 'confirmed').map((pending) => <div className="assistant-pending-action" key={pending.nonce}>
            <div className="assistant-pending-copy">
              <strong>{siteText(pending.display?.action_label || (pending.action === 'revise' ? 'Prepare clip revision' : pending.action === 'analyze' ? 'Prepare clip discovery' : pending.action))}</strong>
              <span>{siteText(pending.target_label || pending.target_id)}</span>
              {pending.display?.effect ? <span>{siteText(pending.display.effect)}</span> : null}
              {pendingPayloadSummary(pending) ? <span>{siteText(pendingPayloadSummary(pending))}</span> : null}
            </div>
            <button type="button" disabled={busy || pending.status === 'confirming' || pending.status === 'failed' || pending.status === 'expired'} onClick={() => void confirmPending(message.id, pending)}>
              {pending.status === 'confirming' ? 'Confirming…' : pending.status === 'failed' || pending.status === 'expired' ? 'Unavailable' : 'Confirm'}
            </button>
          </div>)}
          {message.receipts?.filter((receipt) => receipt.summary).map((receipt) => <div className="assistant-message" key={receipt.nonce}>
            <small>{siteText(`Confirmed · ${receipt.summary}`)}</small>
          </div>)}
          {message.jobs?.map((job) => <div key={job.id} className="assistant-message"><small>{siteText(job.kind === 'revise' ? 'Revision' : job.kind === 'analyze' ? 'Clip discovery' : job.kind)} · {siteText(job.status)}{siteText(job.status === 'running' ? ` · ${Math.round((job.progress || 0) * 100)}%` : '')}</small></div>)}
          {message.clips?.length ? <div className="assistant-clip-results" aria-label="Matching clips">{message.clips.map((clip) => <AssistantClipCard key={clip.id} clip={clip} playing={playingClip === `${index}:${clip.id}`} onPlay={() => setPlayingClip(`${index}:${clip.id}`)} onOpen={() => { onClip(clip.id); setClosing(true) }} />)}</div> : null}
        </div>)}
        {busy ? <div role="status" style={{ color: 'var(--muted)', fontSize: 12 }}>{siteText(status || 'Working…')}</div> : status ? <div role="status">{siteText(status)}</div> : null}
      </div>
      <form className="assistant-compose" onSubmit={(event) => { event.preventDefault(); void submit() }}>
        <label className="sr-only" htmlFor="motion-ai-prompt">Ask Motion AI</label>
        <textarea id="motion-ai-prompt" ref={inputRef} value={prompt} onChange={(event) => setPrompt(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); void submit() } }} rows={2} maxLength={1500} placeholder="What would you like to do?" disabled={busy || !loaded} />
        <div className="assistant-compose-footer"><span className="assistant-compose-brand"><WandSparkles size={15} strokeWidth={1.7} /> Motion</span>{busy ? <button type="button" aria-label="Stop response" onClick={() => void stop()}><X size={17} /></button> : <button type="submit" aria-label="Send prompt" disabled={!loaded || !prompt.trim()}><ArrowUp size={17} /></button>}</div>
      </form>
    </section> : null}
    {!open ? <button ref={launchRef} type="button" className="assistant-launch" aria-label="Open Motion AI" aria-expanded={false} onClick={() => setOpen(true)}><WandSparkles size={18} strokeWidth={1.8} /></button> : null}
  </div>
}

function pendingPayloadSummary(pending: AssistantPendingAction) {
  const parts = pending.display?.version_number != null ? [`Version ${pending.display.version_number}`] : []
  parts.push(...Object.entries(pending.payload || {}).map(([key, value]) => {
    if (value === null || value === undefined || value === '') return null
    if (key === 'version_id' && pending.display?.version_number != null) return null
    const labels: Record<string, string> = {
      instruction: 'Instruction', max_clips: 'Clip limit', duration_s: 'Recording duration', focus: 'Focus',
      assigned_editor_id: 'Editor', speaker_id: 'Speaker', sprout_customer_id: 'Sprout customer', sprout_group_id: 'Sprout group',
    }
    const label = labels[key] || key.replaceAll('_', ' ')
    const rendered = typeof value === 'string' ? value : JSON.stringify(value)
    return `${label}: ${rendered}${key.endsWith('_s') ? ' seconds' : ''}`
  }).filter((value): value is string => Boolean(value)))
  return parts.join(' · ')
}

function AssistantClipCard({ clip, playing, onPlay, onOpen }: { clip: AssistantClip; playing: boolean; onPlay: () => void; onOpen: () => void }) {
  const [posterFailed, setPosterFailed] = useState(false)
  const [thumbnailFailed, setThumbnailFailed] = useState(false)
  const [videoFailed, setVideoFailed] = useState(false)
  const thumbnailRef = useRef<HTMLVideoElement>(null)
  const playerRef = useRef<HTMLVideoElement>(null)
  const intendedPlayingRef = useRef(false)
  const thumbnail = useMediaRecovery(thumbnailRef, clip.preview_url, clip.preview_url)
  const player = useMediaRecovery(playerRef, clip.preview_url, clip.preview_url, intendedPlayingRef)
  useEffect(() => { setPosterFailed(false); setThumbnailFailed(false); setVideoFailed(false) }, [clip.preview_url])
  useEffect(() => { setVideoFailed(false); intendedPlayingRef.current = Boolean(playing) }, [playing])
  return <article className="assistant-clip">
    <button type="button" className="assistant-clip-choice" onClick={clip.preview_url ? onPlay : onOpen} aria-label={siteText(`${clip.preview_url ? 'Play' : 'Open'} ${clip.title}`)}>
      <span className="assistant-clip-thumb">{clip.thumbnail_url && !posterFailed ? <img src={clip.thumbnail_url} alt="" loading="lazy" onError={() => setPosterFailed(true)} /> : clip.preview_url && thumbnail.src && !thumbnailFailed ? <video key={thumbnail.key} ref={thumbnailRef} src={thumbnail.src} muted playsInline preload="metadata" aria-hidden="true" tabIndex={-1} onLoadedMetadata={thumbnail.onLoadedMetadata} onError={(event) => { if (!thumbnail.onError(event)) setThumbnailFailed(true) }} /> : <Film size={24} aria-hidden="true" />}{clip.preview_url ? <span className="assistant-clip-play"><Play size={14} fill="currentColor" aria-hidden="true" /></span> : null}</span>
      <span className="assistant-clip-copy"><strong>{siteText(clip.title)}</strong><span>{speakerCredit(clip.speaker || 'Unknown speaker')}{siteText(clip.duration != null ? ` · ${formatDuration(clip.duration)}` : '')}</span></span>
    </button>
    {playing && clip.preview_url ? <div className="assistant-clip-player">{videoFailed || !player.src ? <p role="status">Video unavailable.</p> : <video key={player.key} ref={playerRef} src={player.src} poster={clip.thumbnail_url || undefined} controls autoPlay playsInline preload="metadata" aria-label={siteText(clip.title)} onLoadedMetadata={player.onLoadedMetadata} onPlay={() => { intendedPlayingRef.current = true }} onPause={(event) => { if (!event.currentTarget.error) intendedPlayingRef.current = false }} onError={(event) => { if (!player.onError(event)) setVideoFailed(true) }} />}<button type="button" onClick={onOpen}>Open in Review <ArrowUpRight size={13} aria-hidden="true" /></button></div> : null}
  </article>
}
